import {createStore as createZustandStore} from 'zustand/vanilla';
import {useStore} from 'zustand';
import logger from 'src/utils/logger';
import type {ChatPanelHandle} from './chat-panel';
import type {
  AskAIExtraContext,
  AskAIExtraContextKey,
} from './askai-extra-context-registry';


export type AskAIState = {
  handle: ChatPanelHandle | null;
  product: string; // product/source key for AskAI
  threadId: string | null;
  shouldFetchStarterPrompts: boolean;
  /**
   * Extra context to attach to each message under `context.page_context`.
   * Sticky for the current thread; cleared on thread reset/change.
   */
  extraPageContext: AskAIExtraContext;
  setHandle: (h: ChatPanelHandle | null) => void;
  setProduct: (p: string) => void;
  setThreadId: (id: string | null) => void;
  clearThreadId: () => void;
  setShouldFetchStarterPrompts: (value: boolean) => void;
  setExtraPageContext: (ctx: AskAIExtraContext) => void;
  patchExtraPageContext: (patch: AskAIExtraContext) => void;
  removeExtraPageContextKey: (key: AskAIExtraContextKey) => void;
  clearExtraPageContext: () => void;
};

const THREAD_ID_STORAGE_KEY = 'askai:threadId';

export const askAIStore = createZustandStore<AskAIState>((set) => {
  let initialThreadId = null;
  try {
    initialThreadId = localStorage.getItem(THREAD_ID_STORAGE_KEY) ?? null;
  } catch (e) {
    logger.error('Error getting initial thread id from localStorage:', e);
  }

  return {
    handle: null,
    product: 'workflow', //default product. can be updated later.
    threadId: initialThreadId,
    shouldFetchStarterPrompts: true,
    extraPageContext: {},
    setHandle: (value) => set({handle: value}),
    setProduct: (value) => set({product: value}),
    setThreadId: (id) => {
      if (typeof localStorage !== 'undefined') {
        if (id) {
          localStorage.setItem(THREAD_ID_STORAGE_KEY, id);
        } else {
          localStorage.removeItem(THREAD_ID_STORAGE_KEY);
        }
      }
      set({threadId: id});
    },
    clearThreadId: () => {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(THREAD_ID_STORAGE_KEY);
      }
      getChatPanelHandle()?.clearMessages();
      set({threadId: null});
    },
    setShouldFetchStarterPrompts: (value: boolean) =>
      set({shouldFetchStarterPrompts: value}),
    setExtraPageContext: (ctx) => set({extraPageContext: {...(ctx ?? {})}}),
    patchExtraPageContext: (patch) =>
      set((s) => ({
        extraPageContext: {...s.extraPageContext, ...(patch ?? {})},
      })),
    removeExtraPageContextKey: (key) =>
      set((s) => {
        const next = {...s.extraPageContext};
        delete next[key];
        return {extraPageContext: next};
      }),
    clearExtraPageContext: () => set({extraPageContext: {}}),
  };
});

// Vanilla accessors (usable outside React)
export function getChatPanelHandle(): ChatPanelHandle | null {
  return askAIStore.getState().handle;
}

export function setChatPanelHandle(value: ChatPanelHandle | null) {
  askAIStore.getState().setHandle(value);
}

export function getProduct(): string | undefined {
  return askAIStore.getState().product;
}

export function getAskAIExtraPageContext(): AskAIExtraContext {
  return askAIStore.getState().extraPageContext;
}

/**
 * Patch one or more key/value pairs to be sent with every message under
 * `context.page_context`.
 */
export function patchAskAIExtraPageContext(patch: AskAIExtraContext) {
  askAIStore.getState().patchExtraPageContext(patch);
}

/** Convenience helper for a single key/value. */
export function setAskAIExtraPageContextValue<K extends AskAIExtraContextKey>(
  key: K,
  value: AskAIExtraContextRegistry[K],
) {
  askAIStore
    .getState()
    .patchExtraPageContext({[key]: value} as AskAIExtraContext);
}

export function removeAskAIExtraPageContextKey(key: AskAIExtraContextKey) {
  askAIStore.getState().removeExtraPageContextKey(key);
}

export function clearAskAIExtraPageContext() {
  askAIStore.getState().clearExtraPageContext();
}

export function setProduct(value?: string) {
  const next = value ?? askAIStore.getState().product;
  askAIStore.getState().setProduct(next);
}
export function registerChatPanel(value: ChatPanelHandle | null) {
  setChatPanelHandle(value);
}

export type PanelMode = 'floating' | 'attached' | 'fullscreen' | 'hidden';

export function getChatPanelMode(): PanelMode | undefined {
  return askAIStore.getState().handle?.currentMode as PanelMode | undefined;
}

export function setChatPanelMode(mode: Exclude<PanelMode, 'attached'>) {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setMode(mode);
}

export function openChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setOpen(true);
}

export function closeChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setOpen(false);
}

export function openMinimizedFloatingChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setMode('floating');
  handle.setOpen(false);
}

export function sendMessage(message: string) {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.openWithMessage([{type: 'plain_text', data: message}]);
}
export function openMaximizedFloatingChatPanel() {
  const handle = askAIStore.getState().handle;
  if (!handle) {
    return;
  }
  handle.setMode('floating');
  handle.setOpen(true);
}

// React selector helpers
export function useAskAIProduct(): string {
  return useStore(askAIStore, (s) => s.product);
}

export function useAskAIThreadId(): string | null {
  return useStore(askAIStore, (s) => s.threadId);
}

export function useShouldFetchStarterPrompts(): boolean {
  return useStore(
    askAIStore,
    (s) => s.shouldFetchStarterPrompts && s.threadId == null,
  );
}

// Vanilla accessors for thread id
export function getThreadId(): string | null {
  return askAIStore.getState().threadId;
}

export function setThreadId(value: string | null) {
  askAIStore.getState().setThreadId(value);
}

export function clearThreadId() {
  askAIStore.getState().clearThreadId();
}

export function initNewThread({
  source,
  shouldFetchStarterPrompts,
  extraPageContext,
}: {
  source: string;
  shouldFetchStarterPrompts: boolean;
  extraPageContext?: AskAIExtraContext;
}) {
  clearThreadId();
  askAIStore.getState().setProduct(source);
  askAIStore.getState().setShouldFetchStarterPrompts(shouldFetchStarterPrompts);
  askAIStore.getState().patchExtraPageContext(extraPageContext ?? {});
}
